import React, { Fragment, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSearchParams } from "react-router-dom"
import { useStoreApi } from "reactflow"

import OpenInFullIcon from "@mui/icons-material/OpenInFull"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import TokenOutlinedIcon from "@mui/icons-material/TokenOutlined"
import { Grid } from "@mui/material"
import { Button } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { ValueBlock } from "../../../../components/data-display/ValueBlock"
import { getTheme } from "../../../../hooks/UseTheme"
import { KonanAPI } from "../../../../services/KonanAPI"
import type { Deployment } from "../../../../types/generated/api/Deployment"
import type { DeploymentList } from "../../../../types/generated/api/DeploymentList"
import type { ModelDeploymentSchemaFeature } from "../../../../types/generated/api/ModelDeploymentSchemaFeature"
import type { PaginatedDeploymentListList } from "../../../../types/generated/api/PaginatedDeploymentListList"
import type { schema } from "../../../../types/generated/api/Schema"
import type { SchemaSourceModel } from "../../../../types/generated/api/SchemaSourceModel"
import { ProjectFeatureMappingDialog } from "../../../projects"
import { useSelectionNodeForm } from "../../hooks/forms/useSelectionNodeForm"
import { useNodeDeletion } from "../../hooks/useNodeHooks"
import type { ProjectNodeProps, SelectionDataItemType } from "../../types/interfaces"
import { nodeHelpers } from "../../utils/nodeHelpers"
import { SelectDataMenu } from "./SelectDataMenu"
import { BaseWorkflowNode } from "./base/BaseWorkflowNode"

/**
 * ProjectNode component handles live model (project) selection and configuration in workflows.
 * Supports project selection, feature mapping, and external navigation.
 */
export function ProjectNode(props: Readonly<ProjectNodeProps>): React.ReactElement {
  // Prop destructuring
  const { data, menuView, id } = props
  const updateGraph = props.data?.updateGraph
  const readMode: boolean | null = data?.readMode ?? null
  const nodeId = id ?? null
  const currentDataItemId = data?.project

  // Hooks (navigation, theme, translation, React Flow)
  const [searchParams] = useSearchParams()
  const theme = getTheme()
  const { t } = useTranslation("workflows", {
    keyPrefix: "selectionNode",
  })
  const store = useStoreApi()
  const { setNodes, getNodes } = store.getState()

  // Constants
  const page = 1
  const pageSize = 1000

  // State hooks
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState<boolean>(false)
  const [isDataProcessing, setIsDataProcessing] = useState<boolean>(false)
  const [selectedValue, setSelectedValue] = useState<SelectionDataItemType | undefined | string>(
    currentDataItemId
      ? typeof currentDataItemId === "string"
        ? currentDataItemId
        : (currentDataItemId as DeploymentList)?.uuid
      : "",
  )

  // Data fetching queries
  const { isLoading: isProjectsLoading, data: projectsData } = useQuery<
    AxiosResponse<PaginatedDeploymentListList>,
    AxiosError
  >(
    ["projects", page, pageSize],
    () =>
      KonanAPI.fetchProjects({
        page: 1,
        pageSize: 1000,
        category: "model",
      }),
    {
      enabled: !readMode && !menuView && data?.nodeType === "ProjectNode",
    },
  )

  const { data: projectData, isLoading: isProjectLoading } = useQuery<AxiosResponse<Deployment>, AxiosError>(
    ["project", selectedValue],
    () => KonanAPI.fetchProject(selectedValue as string),
    {
      onSuccess: ({ data }) => {
        setIsDataProcessing(true)
        const liveModelSchema = data?.schema?.filter((item) =>
          (item as ModelDeploymentSchemaFeature).source_models.some((item: SchemaSourceModel) => item.state === "live"),
        )

        formik?.setFieldValue("liveModelSchema", liveModelSchema)

        const updatedFeatureMappings =
          liveModelSchema.map((feature) => ({
            node_feature: feature?.name,
            workflow_feature:
              props?.data?.feature_mappings?.find((feat) => feat.node_feature === feature.name)?.workflow_feature ?? "",
            type: feature?.type ?? data?.schema?.find((input) => input?.name === (feature as schema).name)?.type,
          })) ?? []

        formik?.setFieldValue("feature_mappings", updatedFeatureMappings)
        formik?.setFieldValue("retrievedNode", {
          uuid: formik?.values?.retrievedNode?.uuid,
          feature_mappings: updatedFeatureMappings,
        })

        // first we check if workflow not in read/view mode so it's either in create or edit
        if (!readMode && selectedValue) {
          // then we check if our cached scripts, has the current script uuid as a property or not
          if (formik?.values?.cachedNodes?.hasOwnProperty(selectedValue as string)) {
            // if already this script is cached then, update feature_mappings state with the cached/saved value
            formik?.setFieldValue("feature_mappings", formik?.values?.cachedNodes[selectedValue as string])
          }
          // if not, and workflow in create mode or the retrieved script uuid not equal to the current one
          // then we add its values as a new cached script item
          else if (
            (searchParams.get("workflowId") === "new" || formik?.values?.retrievedNode?.uuid !== selectedValue) &&
            !formik?.values?.cachedNodes?.hasOwnProperty(selectedValue as string)
          ) {
            const newCachedNodes = {
              ...formik?.values?.cachedNodes,
              [selectedValue as string]: updatedFeatureMappings,
            }

            formik?.setFieldValue("liveModelSchema", liveModelSchema)
            formik?.setFieldValue("cachedNodes", newCachedNodes)
            formik?.setFieldValue("feature_mappings", updatedFeatureMappings)
          }
        }

        setIsDataProcessing(false)
      },
      enabled: !!selectedValue && data?.nodeType === "ProjectNode" && isProjectDialogOpen,
    },
  )

  // Custom hooks
  const formik = useSelectionNodeForm({
    data: props?.data,
    nodeId,
    currentDataItemId: () => currentDataItemId,
  })

  const { handleRemoveNode } = useNodeDeletion(nodeId as string, updateGraph)

  // Node metadata (from centralized helpers)
  const metadata = nodeHelpers.getNodeMetadata("ProjectNode")

  // Helper functions
  const currentData = (): PaginatedDeploymentListList | undefined => {
    return projectsData?.data
  }

  const handleUpdateComputedFeature = (newFeature: string): void => {
    // check if this node has already an assigned computed feat name
    const computedFeatureIndex = props?.data?.formik?.values?.computedFeatures?.findIndex(
      (feature: { uuid: string; value: string }) => feature?.value?.toLowerCase() === newFeature?.toLowerCase(),
    )

    // check if it's a new computed feat name
    if (computedFeatureIndex === -1 && newFeature) {
      props?.data?.formik?.setFieldValue("computedFeatures", [
        ...(props?.data?.formik?.values?.computedFeatures ?? []),
        { uuid: nodeId, value: newFeature },
      ])
    } else if (computedFeatureIndex !== -1 && newFeature) {
      props?.data?.formik?.setFieldValue(`computedFeatures[${computedFeatureIndex}].value`, newFeature)
    }
  }

  // UseEffects
  /**
   * Effect: Updates node state when feature mappings change
   * Listens to changes in project node and updates the node state
   * Only runs when creating or updating workflow (not in read mode)
   */
  useEffect(() => {
    if (formik?.values?.feature_mappings && nodeId) {
      const { feature_mappings } = formik.values

      setTimeout(() => {
        setNodes(
          getNodes().map((node) => {
            const newNode = { ...node }
            if (node.id === nodeId) {
              newNode.data = {
                ...newNode.data,
                feature_mappings: feature_mappings ?? [],
              }
            }
            return newNode
          }),
        )
      }, 100)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.nodeType, formik.values?.feature_mappings, getNodes, nodeId])

  return (
    <Fragment>
      {/* Project Feature Mapping Dialog */}
      {isProjectDialogOpen && (
        <ProjectFeatureMappingDialog
          isOpen
          onClose={() => {
            formik.setFieldValue("cachedNodes", {
              ...formik.values.cachedNodes,
              [selectedValue as string]: formik?.values?.feature_mappings,
            })
            setIsProjectDialogOpen(false)
          }}
          name={projectData?.data.name as string}
          isLoading={isProjectLoading || isDataProcessing}
          workflowSchema={props?.data?.formik?.values?.features}
          workflowFormik={formik}
          isWorkflowInReadMode={readMode}
        />
      )}

      {/* Main Node Card */}
      <BaseWorkflowNode
        id={nodeId as string}
        menuView={menuView}
        readMode={readMode ?? undefined}
        nodeType={data?.nodeType as string}
        title={t("nodeTitles.liveModel")}
        iconProps={{
          element: <TokenOutlinedIcon sx={{ color: theme.palette.base.text[1] }} />,
          className: metadata.iconClassName,
        }}
        handlesProps={{ showTarget: !menuView, showSource: !menuView }}
        width={metadata.width}
        onRemove={() => handleRemoveNode()}
      >
        <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
          <Grid item xs={selectedValue ? 8 : 10}>
            {!menuView && !readMode ? (
              <SelectDataMenu
                nodeId={nodeId}
                dataType="ProjectNode"
                menuView={menuView}
                dataItemId={currentDataItemId}
                itemData={currentData()}
                key={nodeId}
                selectedValue={selectedValue as string}
                setSelectedValue={(value) => {
                  setSelectedValue(value as string)
                  handleUpdateComputedFeature(value as string)
                }}
                isDataLoading={isProjectsLoading}
              />
            ) : (
              <ValueBlock
                value={(currentDataItemId as DeploymentList)?.name ?? t("nodeBodies.noProjectSelected")}
                size={12}
                showTooltip={!menuView}
              />
            )}
          </Grid>

          {/* Action buttons */}
          <Grid container item xs={selectedValue ? 4 : 2} columnSpacing={0.5} ml={0}>
            {selectedValue && (
              <Grid item xs={6}>
                <Button
                  onClick={() => {
                    if (data?.nodeType === "ProjectNode") {
                      setIsProjectDialogOpen(true)
                    }
                  }}
                  disabled={menuView || !selectedValue}
                  size={"regular"}
                  fullWidth
                  data-mode={readMode ? "read" : menuView ? "edit-view" : "edit"}
                  variant="secondary"
                >
                  {data?.project ? (
                    <SettingsOutlinedIcon fontSize="small" sx={{ color: theme.palette.base.text[1] }} />
                  ) : (
                    <OpenInFullIcon fontSize="small" sx={{ color: theme.palette.base.text[1] }} />
                  )}
                </Button>
              </Grid>
            )}
            <Grid item xs={selectedValue ? 6 : 12}>
              <Button
                onClick={() =>
                  window.open(
                    `/projects/${
                      typeof currentDataItemId === "string"
                        ? currentDataItemId
                        : (currentDataItemId as DeploymentList)?.uuid
                    }/decision-engines?page=Models`,
                    "_blank",
                  )
                }
                disabled={menuView}
                size={"regular"}
                fullWidth
                data-mode={readMode ? "read" : menuView ? "edit-view" : "edit"}
                variant="secondary"
              >
                <OpenInFullIcon fontSize="small" sx={{ color: theme.palette.base.text[1] }} />
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </BaseWorkflowNode>
    </Fragment>
  )
}
